import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  MessageSquare,
  Send,
  Upload,
  Users,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Trash2,
  History,
  Megaphone,
  Settings as SettingsIcon,
  KeyRound,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatusBadge = ({ status }) => {
  const map = {
    sent: { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2, label: 'Envoyé' },
    mock: { color: 'bg-amber-100 text-amber-700 border-amber-300', icon: AlertCircle, label: 'Mock (test)' },
    failed: { color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'Échec' },
    invalid_number: { color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'N° invalide' },
  };
  const s = map[status] || { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: AlertCircle, label: status };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${s.color}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
};

const SMSManagement = () => {
  const { token } = useAuth();
  const authHeader = { Authorization: `Bearer ${token}` };

  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Test form
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Test SMS depuis Pharmacie Saint Ange Des Amis.');
  const [sendingTest, setSendingTest] = useState(false);

  // Campaign form
  const [campaignMessage, setCampaignMessage] = useState('');
  const [campaignSource, setCampaignSource] = useState('manual');
  const [manualPhones, setManualPhones] = useState('');
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [sourceCounts, setSourceCounts] = useState(null);

  // Contacts import
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // History
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);

  // Portal credentials (Orange SMSAffaires)
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [credForm, setCredForm] = useState({ login: '', password: '', confirm: '', test_sms: false });
  const [credSaving, setCredSaving] = useState(false);
  const [credTestResult, setCredTestResult] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/sms/settings`, { headers: authHeader });
      setSettings(data);
    } catch (e) {
      toast.error('Erreur chargement paramètres SMS');
    } finally {
      setLoadingSettings(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSourceCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/sms/contacts/from-db`, { headers: authHeader });
      setSourceCounts(data);
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/sms/logs?limit=50`, { headers: authHeader });
      setLogs(data.items || []);
      setLogsTotal(data.total || 0);
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchSettings();
    fetchSourceCounts();
    fetchLogs();
    // Load credentials metadata (login only, never password)
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/admin/sms/credentials`, { headers: authHeader });
        setCredentials(data);
        setCredForm((f) => ({ ...f, login: data.login || '' }));
      } catch (e) {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSettings, fetchSourceCounts, fetchLogs]);

  const saveCredentials = async () => {
    setCredTestResult(null);
    if (!credForm.password) {
      toast.error('Nouveau mot de passe requis');
      return;
    }
    if (credForm.password !== credForm.confirm) {
      toast.error('Les deux mots de passe ne correspondent pas');
      return;
    }
    setCredSaving(true);
    try {
      const payload = {
        login: credForm.login || null,
        password: credForm.password,
        test_sms_phone: credForm.test_sms && testPhone ? testPhone : null,
      };
      const { data } = await axios.put(`${API_URL}/admin/sms/credentials`, payload, { headers: authHeader });
      toast.success(data.message || 'Identifiants mis à jour');
      setCredTestResult(data.test_sms);
      setCredForm({ login: credForm.login, password: '', confirm: '', test_sms: false });
      // Refresh credentials metadata
      const r = await axios.get(`${API_URL}/admin/sms/credentials`, { headers: authHeader });
      setCredentials(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setCredSaving(false);
    }
  };

  const testCredentialsOnly = async () => {
    setCredTestResult(null);
    if (!credForm.password) {
      toast.error('Entrez un mot de passe à tester');
      return;
    }
    setCredSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/sms/credentials/test`,
        { login: credForm.login || null, password: credForm.password },
        { headers: authHeader }
      );
      if (data.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de test');
    } finally {
      setCredSaving(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/admin/sms/settings`,
        { [key]: value },
        { headers: authHeader }
      );
      setSettings((prev) => ({ ...prev, ...data }));
      toast.success('Paramètres mis à jour');
    } catch (e) {
      toast.error('Erreur mise à jour');
    }
  };

  const sendTest = async () => {
    if (!testPhone || !testMessage) {
      toast.error('Numéro et message requis');
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/sms/test`,
        { phone: testPhone, message: testMessage },
        { headers: authHeader }
      );
      if (data.success) {
        const label = data.status === 'mock' ? 'MOCK (pas d\'envoi réel)' : 'Envoyé';
        toast.success(`SMS ${label}`);
      } else {
        toast.error(`Échec: ${data.error || 'inconnu'}`);
      }
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSendingTest(false);
    }
  };

  const sendCampaign = async () => {
    if (!campaignMessage) {
      toast.error('Message requis');
      return;
    }
    const phones =
      campaignSource === 'manual'
        ? manualPhones
            .split(/[\n,;]/)
            .map((p) => p.trim())
            .filter(Boolean)
        : null;
    if (campaignSource === 'manual' && (!phones || phones.length === 0)) {
      toast.error('Ajoutez au moins un numéro');
      return;
    }
    if (!window.confirm(`Confirmer l'envoi de la campagne ?`)) return;
    setSendingCampaign(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/sms/campaign`,
        { message: campaignMessage, source: campaignSource, phones },
        { headers: authHeader }
      );
      toast.success(
        `Campagne: ${data.sent} envoyés, ${data.mock} mock, ${data.failed} échecs (total ${data.total})`
      );
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur campagne');
    } finally {
      setSendingCampaign(false);
    }
  };

  const importContacts = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/admin/sms/contacts/import`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Importé: ${data.imported} contacts (${data.skipped} ignorés)`);
      fetchSourceCounts();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Status banner */}
      {!loadingSettings && settings && (
        <Card className={settings.configured && settings.enabled ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}>
          <CardContent className="p-4 flex items-start gap-3">
            {settings.configured && settings.enabled ? (
              <CheckCircle2 className="w-5 h-5 text-green-700 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5" />
            )}
            <div className="flex-1 text-sm">
              {settings.configured && settings.enabled ? (
                <>
                  <p className="font-medium text-green-800">SMS actif — les envois partent réellement</p>
                  <p className="text-green-700 text-xs">
                    API configurée ({settings.api_url_masked}) · Compte: <code>{settings.login}</code> · Expéditeur: <code>{settings.sender_id}</code>
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-amber-800">
                    Mode MOCK — Aucun SMS ne part réellement
                  </p>
                  <p className="text-amber-700 text-xs mt-1">
                    {!settings.configured
                      ? "Configurez d'abord les identifiants Orange ci-dessous, puis activez l'interrupteur."
                      : "Activez l'interrupteur ci-dessous pour envoyer réellement les SMS via Orange SMSAffaires."}
                  </p>
                </>
              )}
            </div>
            {settings.configured && (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-medium text-[#5A4A3D]">
                  {settings.enabled ? 'Actif' : 'Inactif'}
                </span>
                <Switch
                  checked={!!settings.enabled}
                  onCheckedChange={(v) => updateSetting('enabled', v)}
                  data-testid="sms-master-enabled-switch"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== Identifiants Orange (collapsible) ===== */}
      <Card data-testid="sms-credentials-card">
        <CardHeader className="cursor-pointer" onClick={() => setCredentialsOpen((o) => !o)}>
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#C45C26]" />
              Identifiants Orange SMSAffaires
              {credentials?.has_db_override && (
                <Badge variant="outline" className="ml-2 text-xs">Modifié via admin</Badge>
              )}
            </span>
            {credentialsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </CardTitle>
        </CardHeader>
        {credentialsOpen && (
          <CardContent className="space-y-3">
            <p className="text-xs text-[#8B7355]">
              Si vous changez votre mot de passe sur le portail Orange (<a href="https://smsaffaires.orange.ci" target="_blank" rel="noopener noreferrer" className="underline">smsaffaires.orange.ci</a>), mettez-le à jour ici pour que les envois automatiques continuent à fonctionner.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-xs text-blue-800">
              <p><strong>Login actuel :</strong> <code>{credentials?.login || '—'}</code></p>
              {credentials?.last_updated_at && (
                <p className="mt-1">
                  <strong>Dernière modification :</strong>{' '}
                  {new Date(credentials.last_updated_at).toLocaleString('fr-FR')}
                  {credentials.last_updated_by ? ` par ${credentials.last_updated_by}` : ''}
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm">Login portail (rarement modifié)</Label>
              <Input
                value={credForm.login}
                onChange={(e) => setCredForm({ ...credForm, login: e.target.value })}
                placeholder="pharmacie.saint.ange.des.amis"
                data-testid="sms-cred-login"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Nouveau mot de passe</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={credForm.password}
                  onChange={(e) => setCredForm({ ...credForm, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="sms-cred-password"
                />
              </div>
              <div>
                <Label className="text-sm">Confirmation</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={credForm.confirm}
                  onChange={(e) => setCredForm({ ...credForm, confirm: e.target.value })}
                  placeholder="••••••••"
                  data-testid="sms-cred-confirm"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-[#5D4E37] cursor-pointer">
              <input
                type="checkbox"
                checked={credForm.test_sms}
                onChange={(e) => setCredForm({ ...credForm, test_sms: e.target.checked })}
                className="w-4 h-4 accent-[#C45C26]"
                data-testid="sms-cred-test-checkbox"
              />
              <span>
                Envoyer un SMS de test après sauvegarde (vers le numéro saisi dans "Envoi de test" ci-dessous)
              </span>
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                variant="outline"
                onClick={testCredentialsOnly}
                disabled={credSaving || !credForm.password}
                data-testid="sms-cred-test-btn"
              >
                {credSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Tester la connexion
              </Button>
              <Button
                onClick={saveCredentials}
                disabled={credSaving || !credForm.password || credForm.password !== credForm.confirm}
                className="bg-[#C45C26] hover:bg-[#A84B1F]"
                data-testid="sms-cred-save-btn"
              >
                {credSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Tester et sauvegarder
              </Button>
            </div>
            {credTestResult && (
              <div className="text-xs bg-green-50 border border-green-200 rounded-md p-2 text-green-800">
                SMS de test envoyé — statut : <strong>{credTestResult.status}</strong>
                {credTestResult.error && <> (erreur : {credTestResult.error})</>}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Envoi de test */}
      <Card data-testid="sms-test-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Send className="w-5 h-5 text-[#C45C26]" />
            Envoi de test (1 numéro)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Numéro destinataire</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="0757910663 ou +22557910663"
              data-testid="sms-test-phone"
            />
          </div>
          <div>
            <Label>Message ({testMessage.length}/160)</Label>
            <Textarea
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              rows={3}
              maxLength={1000}
              data-testid="sms-test-message"
            />
          </div>
          <Button
            onClick={sendTest}
            disabled={sendingTest}
            className="bg-[#C45C26] hover:bg-[#A84B1F]"
            data-testid="sms-test-send-btn"
          >
            {sendingTest ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Envoyer le test</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Campagne */}
      <Card data-testid="sms-campaign-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Megaphone className="w-5 h-5 text-[#C45C26]" />
            Campagne / Broadcast
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Source des destinataires</Label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={campaignSource}
              onChange={(e) => setCampaignSource(e.target.value)}
              data-testid="sms-campaign-source"
            >
              <option value="manual">Numéros saisis manuellement</option>
              <option value="contacts">
                Contacts SMS importés / opt-in
                {sourceCounts ? ` (${sourceCounts.sms_contacts})` : ''}
              </option>
              <option value="reservations">
                Réservations clients
                {sourceCounts ? ` (${sourceCounts.reservations})` : ''}
              </option>
              <option value="requests">
                Demandes produits
                {sourceCounts ? ` (${sourceCounts.product_requests})` : ''}
              </option>
              <option value="skincare">
                Clients SADASkinCare
                {sourceCounts ? ` (${sourceCounts.skincare_clients ?? 0})` : ''}
              </option>
              <option value="health">
                Patients SADAHealth
                {sourceCounts ? ` (${sourceCounts.health_patients ?? 0})` : ''}
              </option>
              <option value="all_db">
                Tous (toutes sources dédupliquées)
                {sourceCounts
                  ? ` (~${(sourceCounts.sms_contacts || 0) + (sourceCounts.reservations || 0) + (sourceCounts.product_requests || 0) + (sourceCounts.skincare_clients || 0) + (sourceCounts.health_patients || 0)})`
                  : ''}
              </option>
            </select>
          </div>

          {campaignSource === 'manual' && (
            <div>
              <Label>Numéros (1 par ligne, ou séparés par virgule/point-virgule)</Label>
              <Textarea
                value={manualPhones}
                onChange={(e) => setManualPhones(e.target.value)}
                rows={4}
                placeholder={'0757910663\n0708730243\n+22501234567'}
                data-testid="sms-campaign-phones"
              />
            </div>
          )}

          <div>
            <Label>Message ({campaignMessage.length}/160)</Label>
            <Textarea
              value={campaignMessage}
              onChange={(e) => setCampaignMessage(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Ex: Pharmacie Saint Ange Des Amis est de garde ce weekend. Ouvert 24h/24. Tel: 27 22 50 19 19"
              data-testid="sms-campaign-message"
            />
          </div>

          <Button
            onClick={sendCampaign}
            disabled={sendingCampaign}
            className="bg-[#C45C26] hover:bg-[#A84B1F]"
            data-testid="sms-campaign-send-btn"
          >
            {sendingCampaign ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
            ) : (
              <><Megaphone className="w-4 h-4 mr-2" /> Lancer la campagne</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Import contacts */}
      <Card data-testid="sms-import-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-[#C45C26]" />
            Importer une liste de contacts (Excel / CSV)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#8B7355]">
            Colonne attendue : <code>phone</code> (ou <code>telephone</code>, <code>numero</code>).
            Optionnel : <code>name</code> / <code>nom</code>. Les doublons sont fusionnés automatiquement.
          </p>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={importContacts}
            ref={fileInputRef}
            className="hidden"
            id="sms-import-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            variant="outline"
            data-testid="sms-import-btn"
          >
            {importing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Choisir un fichier</>
            )}
          </Button>
          {sourceCounts && (
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">Contacts importés: {sourceCounts.sms_contacts}</Badge>
              <Badge variant="outline">Réservations: {sourceCounts.reservations}</Badge>
              <Badge variant="outline">Demandes produits: {sourceCounts.product_requests}</Badge>
              <Badge variant="outline">SADASkinCare: {sourceCounts.skincare_clients ?? 0}</Badge>
              <Badge variant="outline">SADAHealth: {sourceCounts.health_patients ?? 0}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paramètres auto */}
      <Card data-testid="sms-auto-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <SettingsIcon className="w-5 h-5 text-[#C45C26]" />
            SMS automatiques
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingSettings || !settings ? (
            <p className="text-sm text-[#8B7355]">Chargement...</p>
          ) : (
            <>
              <div className="flex items-center justify-between border-b pb-3">
                <div>
                  <p className="font-medium text-sm">Confirmation de demande</p>
                  <p className="text-xs text-[#8B7355]">SMS au client quand il soumet une demande de produit</p>
                </div>
                <Switch
                  checked={!!settings.auto_order_confirmation}
                  onCheckedChange={(v) => updateSetting('auto_order_confirmation', v)}
                  data-testid="sms-auto-confirmation-switch"
                />
              </div>
              <div className="flex items-center justify-between border-b pb-3">
                <div>
                  <p className="font-medium text-sm">Commande prête à retirer</p>
                  <p className="text-xs text-[#8B7355]">SMS quand vous passez une demande au statut "Traitée / terminée"</p>
                </div>
                <Switch
                  checked={!!settings.auto_order_ready}
                  onCheckedChange={(v) => updateSetting('auto_order_ready', v)}
                  data-testid="sms-auto-ready-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Notification de garde</p>
                  <p className="text-xs text-[#8B7355]">À activer quand vous êtes de garde (envoi manuel via "Campagne")</p>
                </div>
                <Switch
                  checked={!!settings.auto_garde_notification}
                  onCheckedChange={(v) => updateSetting('auto_garde_notification', v)}
                  data-testid="sms-auto-garde-switch"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Historique */}
      <Card data-testid="sms-logs-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="w-5 h-5 text-[#C45C26]" />
            Historique des envois ({logsTotal})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-[#8B7355]">Aucun envoi pour l'instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[#8B7355]">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Destinataire</th>
                    <th className="py-2 pr-3">Message</th>
                    <th className="py-2 pr-3">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b hover:bg-gray-50" data-testid="sms-log-row">
                      <td className="py-2 pr-3 whitespace-nowrap text-xs text-[#8B7355]">
                        {new Date(l.sent_at).toLocaleString('fr-FR')}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">
                        {l.normalized_to || l.to}
                      </td>
                      <td className="py-2 pr-3 text-xs max-w-xs truncate" title={l.message}>
                        {l.message}
                      </td>
                      <td className="py-2 pr-3">
                        <StatusBadge status={l.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SMSManagement;
